import { toolDefinition } from '@tanstack/ai';
import { z } from 'zod';

import type { CatalogRow, ChartResultRow } from '../types/chat.types';

const catalogRowSchema = z.object({
  code: z.string(),
  title: z.string(),
  trading_hub: z.string(),
  commodity: z.string(),
  units: z.string(),
  frequency: z.string(),
  pricing_basis: z.string(),
  period_type: z.string(),
  time_ref: z.number().optional(),
  period_max: z.number(),
  period_min: z.number()
}) satisfies z.ZodType<CatalogRow>;

const chartResultRowSchema = z.object({
  code: z.string(),
  title: z.string(),
  period_type: z.string(),
  time_ref: z.union([z.string(), z.number()]).optional(),
  period: z.string().optional(),
  group: z.string().optional()
}) satisfies z.ZodType<ChartResultRow>;

const dateMetadataSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional()
});

export const renderTableOutputSchema = z.object({
  rows: z.array(catalogRowSchema),
  metadata: dateMetadataSchema.optional()
});

export const renderDefaultOutputSchema = renderTableOutputSchema;

export const renderSpotChartOutputSchema = z.object({
  title: z.string().optional(),
  rows: z.array(chartResultRowSchema)
});

export const renderSeasonalChartOutputSchema = z.object({
  title: z.string().optional(),
  from: z.string().optional(),
  rows: z.array(chartResultRowSchema)
});

export const renderForwardChartOutputSchema = z.object({
  title: z.string().optional(),
  rows: z.array(chartResultRowSchema)
});

export const renderFileChartOutputSchema = z.object({
  title: z.string().optional(),
  seriesFileName: z.string(),
  configFileName: z.string(),
  queryFileId: z.string().optional(),
  downloadFileId: z.string().optional(),
  timeRangeButtons: z
    .array(z.object({ label: z.string(), value: z.string(), default: z.boolean().optional() }))
    .optional()
});

// Mirrors STATIC_CHART_RESPONSE_CONTRACT.md: a complete, JSON-serializable
// Highcharts config the frontend never transforms. `chartConfig` stays
// `z.record` (not a full Highcharts schema) — the contract doc's job is to
// keep the *shape rules* (no JS expressions, ms timestamps, etc.) enforced
// by the backend; this schema's job is only "is there a config object at all".
export const renderStaticChartOutputSchema = z.object({
  title: z.string().optional(),
  chartConfig: z.record(z.string(), z.unknown())
});

const noInput = z.object({});

export const renderTableTool = toolDefinition({
  name: 'render_table',
  description: 'Renders a catalogue table of GX symbols already resolved by the backend.',
  inputSchema: noInput,
  outputSchema: renderTableOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderTableOutputSchema>);

export const renderDefaultTool = toolDefinition({
  name: 'render_default',
  description: 'Renders a catalogue table with no specific display styling.',
  inputSchema: noInput,
  outputSchema: renderDefaultOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderDefaultOutputSchema>);

export const renderSpotChartTool = toolDefinition({
  name: 'render_spot_chart',
  description: 'Renders a spot price chart already resolved by the backend.',
  inputSchema: noInput,
  outputSchema: renderSpotChartOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderSpotChartOutputSchema>);

export const renderSeasonalChartTool = toolDefinition({
  name: 'render_seasonal_chart',
  description: 'Renders a seasonal comparison chart already resolved by the backend.',
  inputSchema: noInput,
  outputSchema: renderSeasonalChartOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderSeasonalChartOutputSchema>);

export const renderForwardChartTool = toolDefinition({
  name: 'render_forward_chart',
  description: 'Renders a forward curve chart already resolved by the backend.',
  inputSchema: noInput,
  outputSchema: renderForwardChartOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderForwardChartOutputSchema>);

export const renderFileChartTool = toolDefinition({
  name: 'render_file_chart',
  description: 'Renders a file-backed chart already resolved by the backend.',
  inputSchema: noInput,
  outputSchema: renderFileChartOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderFileChartOutputSchema>);

export const renderStaticChartTool = toolDefinition({
  name: 'render_static_chart',
  description:
    'Renders a fully backend-resolved Highcharts chart (see STATIC_CHART_RESPONSE_CONTRACT.md).',
  inputSchema: noInput,
  outputSchema: renderStaticChartOutputSchema
}).server(async args => args as unknown as z.infer<typeof renderStaticChartOutputSchema>);

export const displayTools = [
  renderTableTool,
  renderDefaultTool,
  renderSpotChartTool,
  renderSeasonalChartTool,
  renderForwardChartTool,
  renderFileChartTool,
  renderStaticChartTool
];
